import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/services/api";
import { CheckCircle2, Copy, Loader2, Plus, RefreshCw, ShieldAlert, Sparkles, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const MCP_URL = "https://mcp.baitaticket.com.br/mcp";

interface McpTokenItem {
  id: string;
  label?: string;
  tokenPrefix: string;
  createdAt: string;
  lastUsedAt?: string;
  revoked: boolean;
}

// ─── Copy button (mesmo padrão de WhatsAppSettings.tsx) ────────────────────────

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Não foi possível copiar");
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 rounded-md border border-border p-2 transition-colors hover:bg-accent"
      aria-label="Copiar"
    >
      {copied ? (
        <CheckCircle2 className="h-4 w-4 text-green-500" />
      ) : (
        <Copy className="h-4 w-4 text-muted-foreground" />
      )}
    </button>
  );
}

function CodeBlock({ value }: { value: string }) {
  return (
    <div className="flex items-start gap-2">
      <pre className="flex-1 overflow-x-auto whitespace-pre-wrap break-all rounded-md border border-border bg-muted px-3 py-2 text-sm font-mono">
        {value}
      </pre>
      <CopyButton value={value} />
    </div>
  );
}

function StepCard({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border p-6">
      <div className="mb-3 flex items-center gap-3">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
          {number}
        </span>
        <h2 className="font-semibold">{title}</h2>
      </div>
      <div className="space-y-3 pl-9">{children}</div>
    </div>
  );
}

function formatDate(value?: string) {
  if (!value) return "nunca usado";
  return new Date(value).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

// ─── Página ─────────────────────────────────────────────────────────────────

export default function McpSettings() {
  const [version, setVersion] = useState<string | null>(null);
  const [tokens, setTokens] = useState<McpTokenItem[] | null>(null);
  const [freshCommand, setFreshCommand] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const loadTokens = useCallback(async () => {
    try {
      const res = await api.get<McpTokenItem[]>("/app/mcp-tokens");
      setTokens(res.data.filter((t) => !t.revoked));
    } catch {
      setTokens([]);
    }
  }, []);

  useEffect(() => {
    void loadTokens();
    async function loadVersion() {
      try {
        const res = await api.get("/public/mcp/version");
        setVersion(res.data?.version || null);
      } catch {
        // silencioso — a versão é só informativa
      }
    }
    void loadVersion();
  }, [loadTokens]);

  async function handleGenerate() {
    setGenerating(true);
    try {
      const res = await api.post<{ id: string; token: string }>("/app/mcp-tokens", {});
      const command = `claude mcp add --transport http baitaticket ${MCP_URL} --header "Authorization: Bearer ${res.data.token}"`;
      setFreshCommand(command);
      await loadTokens();
    } catch {
      toast.error("Não foi possível gerar o token. Tente de novo.");
    } finally {
      setGenerating(false);
    }
  }

  async function handleRevoke(id: string) {
    setRevokingId(id);
    try {
      await api.delete(`/app/mcp-tokens/${id}`);
      toast.success("Token revogado");
      await loadTokens();
    } catch {
      toast.error("Não foi possível revogar. Tente de novo.");
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold">Baita Ticket para o Claude</h1>
        </div>
        <p className="text-muted-foreground text-sm mt-1">
          Dá ao Claude acesso a tickets, conversas, canais, agentes, templates de WhatsApp e
          cobrança — pra diagnosticar e resolver problemas direto na conversa. Só agentes têm
          acesso a essa integração.
        </p>
      </div>

      <div className="space-y-4">
        <StepCard number={1} title="Gerar um token e copiar o comando">
          <p className="text-sm text-muted-foreground">
            Cada token é só pra um lugar (seu notebook, por exemplo) e pode ser revogado a
            qualquer momento sem afetar os outros — não é a senha da sua conta.
          </p>
          {freshCommand ? (
            <>
              <CodeBlock value={freshCommand} />
              <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Copie agora — o token não aparece de novo depois que você sair desta página.
                </p>
              </div>
            </>
          ) : (
            <Button className="gap-2" onClick={handleGenerate} disabled={generating}>
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Gerar token e comando
            </Button>
          )}
        </StepCard>

        <StepCard number={2} title="Colar no terminal">
          <p className="text-sm text-muted-foreground">
            Com o Claude Code instalado, cole o comando do passo 1 no terminal. Pronto — já pode
            pedir pro Claude ver seus tickets, conversas e o resto.
          </p>
        </StepCard>
      </div>

      <div className="mt-6 rounded-lg border border-border p-6">
        <h2 className="mb-1 font-semibold">Tokens ativos</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Um por lugar onde o Claude está conectado. Revogue os que não usa mais.
        </p>
        {tokens === null ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : tokens.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum token ativo ainda.</p>
        ) : (
          <div className="space-y-2">
            {tokens.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2.5"
              >
                <div className="min-w-0">
                  <code className="text-sm font-mono">{t.tokenPrefix}…</code>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Criado em {formatDate(t.createdAt)} · Último uso: {formatDate(t.lastUsedAt)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-1.5 text-destructive hover:text-destructive"
                  onClick={() => handleRevoke(t.id)}
                  disabled={revokingId === t.id}
                >
                  {revokingId === t.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                  Revogar
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-4">
        <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          {version ? (
            <>
              Versão do servidor: <code className="rounded bg-background px-1.5 py-0.5">{version}</code>.{" "}
            </>
          ) : null}
          Atualiza sozinho — nenhum token precisa ser trocado quando publicarmos uma versão nova.
        </p>
      </div>
    </>
  );
}
